import React from 'react';
import { X } from 'lucide-react';
import { Tag } from '../../types';

interface TagPillProps {
  tag: Tag;
  onClick?: (e: React.MouseEvent) => void;
  /** Renders a trailing × button. Independent of onClick — chip mode. */
  onRemove?: () => void;
  selected?: boolean;
  size?: 'sm' | 'md';
}

/**
 * Student-facing tag pill. Honors `tag.color` (hex) when present, with a neutral
 * gray fallback. Used on course cards, course detail headers, and filter bars.
 */
export const TagPill: React.FC<TagPillProps> = ({ tag, onClick, onRemove, selected, size = 'sm' }) => {
  const sizeClasses =
    size === 'md' ? 'px-3 py-1 text-xs' : 'px-2.5 py-0.5 text-[11px]';

  const interactive = !!onClick;

  // Apply color via inline style when set; otherwise use a neutral gray scheme.
  const style: React.CSSProperties = {};
  if (selected && tag.color) {
    style.backgroundColor = tag.color;
    style.color = '#fff';
    style.borderColor = tag.color;
  } else if (tag.color) {
    style.color = tag.color;
    style.borderColor = tag.color;
    style.backgroundColor = `${tag.color}14`; // ~8% alpha
  }

  const baseClasses = `inline-flex items-center gap-1 rounded-full font-semibold border transition-colors ${sizeClasses}`;
  const fallbackClasses = tag.color
    ? ''
    : selected
    ? 'bg-gray-900 text-white border-gray-900'
    : 'bg-gray-100 text-gray-700 border-gray-200';
  const interactiveClasses = interactive
    ? 'cursor-pointer hover:opacity-80'
    : '';

  const removeButton = onRemove ? (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove();
      }}
      aria-label={`Remove ${tag.name}`}
      className="ml-0.5 -mr-1 inline-flex items-center justify-center rounded-full hover:bg-black/10 p-0.5"
    >
      <X className="h-3 w-3" strokeWidth={2.5} />
    </button>
  ) : null;

  if (interactive) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick(e);
        }}
        className={`${baseClasses} ${fallbackClasses} ${interactiveClasses}`}
        style={style}
        aria-pressed={selected}
      >
        {tag.name}
        {removeButton}
      </button>
    );
  }

  return (
    <span className={`${baseClasses} ${fallbackClasses}`} style={style}>
      {tag.name}
      {removeButton}
    </span>
  );
};
